import { createSlice } from "@reduxjs/toolkit";

// Les totaux sont dérivés des lignes après chaque mutation, plutôt que tenus
// à la main dans chaque réducteur : c'est cette duplication qui avait laissé
// un résidu négatif dans le panier d'un client (voir cartStore.test.js).
function recalculerTotaux(state) {
  state.totalQuantity = state.items.reduce((somme, item) => somme + item.quantity, 0);
  state.totalAmount = state.items.reduce((somme, item) => somme + item.totalPrice, 0);
  state.totalPV = state.items.reduce((somme, item) => somme + item.totalPV, 0);
}

export const cartData = createSlice({
  name: "cart",
  initialState: {
    items: [],
    totalQuantity: 0,
    totalAmount: 0,
    totalPV: 0,
  },
  reducers: {
    addToCart: (state, action) => {
      const prod = action.payload;
      if (
        !prod ||
        prod.name === "COMBO" ||
        prod.description === "ACHAT D'ADHESION" ||
        // A16, 22/09/2026 : la boutique affiche désormais aussi les produits désactivés
        // (grisés) — ce garde-fou empêche de les ajouter par un autre chemin que le bouton
        // déjà neutralisé de ProductGridCard (défense en profondeur, comme pour COMBO).
        prod.status === "inactive"
      ) {
        return;
      }
      const exist = state.items.find((item) => item._id === prod._id);

      const price = prod.price || 0;
      const numPV = prod.numPV || 0;
      // Point 7 (10/09) : le stock disponible du produit, gardé à part de
      // `quantity` (qui, elle, compte ce que le distributeur met au panier) —
      // il sert à borner aussi bien les boutons +/- que la saisie libre.
      const stock = Number(prod.quantity) || 0;

      if (!exist) {
        state.items.push({
          _id: prod._id,
          price,
          totalPV: numPV,
          numPV,
          quantity: 1,
          stock,
          totalPrice: price,
          name: prod.name,
        });
      } else {
        if (stock > 0 && exist.quantity >= stock) return;
        exist.quantity++;
        exist.totalPrice += price;
        exist.totalPV += numPV;
      }

      recalculerTotaux(state);
    },

    removeToCart: (state, action) => {
      const prod = action.payload;
      const exist = state.items.find((item) => item._id === prod._id);

      if (exist) {
        state.items = state.items.filter((e) => e._id !== prod._id);
        recalculerTotaux(state);
      }
    },

    increaseToCart: (state, action) => {
      const prod = action.payload;
      const exist = state.items.find((item) => item._id === prod._id);

      if (exist) {
        // Point 7 (10/09) : ne jamais dépasser le stock disponible — ligne
        // ajoutée avant ce point n'a pas de `stock` connu (panier repris
        // depuis le stockage local), auquel cas on ne borne pas.
        if (exist.stock > 0 && exist.quantity >= exist.stock) return;
        exist.quantity++;
        exist.totalPrice += exist.price;
        exist.totalPV += exist.numPV;
        recalculerTotaux(state);
      }
    },

    // Point 7 (10/09) : saisie libre de la quantité — { _id, quantity }.
    // Toujours ramené à un entier entre 1 et le stock disponible (s'il est
    // connu) ; en dessous de 1, la ligne se comporte comme `decreaseToCart`
    // à la dernière unité et disparaît du panier.
    setQuantity: (state, action) => {
      const { _id, quantity } = action.payload || {};
      const exist = state.items.find((item) => item._id === _id);
      if (!exist) return;

      const demandee = Math.floor(Number(quantity));
      if (!Number.isFinite(demandee) || demandee < 1) {
        state.items = state.items.filter((item) => item._id !== _id);
        recalculerTotaux(state);
        return;
      }

      const bornee = exist.stock > 0 ? Math.min(demandee, exist.stock) : demandee;
      exist.totalPrice = exist.price * bornee;
      exist.totalPV = exist.numPV * bornee;
      exist.quantity = bornee;

      recalculerTotaux(state);
    },

    decreaseToCart: (state, action) => {
      const prod = action.payload;
      const exist = state.items.find((item) => item._id === prod._id);
      if (!exist) return;

      // Dernière unité : retirer la ligne entièrement plutôt que de continuer
      // à décrémenter — sans ce garde-fou, la quantité et le PV de la ligne
      // passaient dans le négatif, sans jamais vider le panier.
      if (exist.quantity <= 1) {
        state.items = state.items.filter((item) => item._id !== prod._id);
      } else {
        exist.quantity--;
        exist.totalPrice -= exist.price;
        exist.totalPV -= exist.numPV;
      }

      recalculerTotaux(state);
    },

    deleteCart: (state) => {
      state.items = [];
      state.totalQuantity = 0;
      state.totalAmount = 0;
      state.totalPV = 0;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  addToCart,
  removeToCart,
  deleteCart,
  decreaseToCart,
  increaseToCart,
  setQuantity,
} = cartData.actions;

export default cartData.reducer;
